import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { getAllDevelopers, createProjectForDeveloper } from '../api/developerManagementApi'

export default function AdminCreateProjectForDeveloper() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState({
    developerId: '',
    title: '',
    description: '',
    location: '',
    address: '',
    status: 'draft'
  })

  const { data: developers, isLoading: developersLoading } = useQuery({
    queryKey: ['admin-developers'],
    queryFn: async () => {
      const { data, error } = await getAllDevelopers()
      if (error) throw error
      return data
    }
  })

  const createMutation = useMutation({
    mutationFn: createProjectForDeveloper,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['admin-projects'])
      alert('Project created successfully!')
      if (data.data?.project_id) {
        navigate(`/admin/projects/${data.data.project_id}`)
      } else {
        navigate('/admin')
      }
    },
    onError: (error) => {
      alert(error.message || 'Failed to create project')
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.developerId) {
      alert('Please select a developer')
      return
    }
    createMutation.mutate(formData)
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-secondary-900">Create Project for Developer</h2>
        <p className="mt-1 text-sm text-secondary-500">
          Set up a new project on behalf of a developer
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">
            Select Developer <span className="text-error-600">*</span>
          </label>
          {developersLoading ? (
            <div className="text-sm text-secondary-500">Loading developers...</div>
          ) : !developers || developers.length === 0 ? (
            <div className="text-sm text-error-600">
              No developers available. Please invite a developer first.
            </div>
          ) : (
            <select
              required
              value={formData.developerId}
              onChange={(e) => handleChange('developerId', e.target.value)}
              className="input-field"
            >
              <option value="">Select a developer...</option>
              {developers.map((dev) => (
                <option key={dev.id} value={dev.id}>
                  {dev.full_name} {dev.company_name ? `(${dev.company_name})` : ''} - {dev.email}
                </option>
              ))}
            </select>
          )}
          <p className="mt-1 text-xs text-secondary-500">
            The selected developer will be the owner of this project
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">
            Project Title <span className="text-error-600">*</span>
          </label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            className="input-field"
            placeholder="e.g., Sunset Apartments"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            className="input-field"
            rows={4}
            placeholder="Describe the project..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">
              Location
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
              className="input-field"
              placeholder="e.g., Auckland, Wellington"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">
              Address
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              className="input-field"
              placeholder="e.g., 123 Main Street"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">
            Initial Status
          </label>
          <select
            value={formData.status}
            onChange={(e) => handleChange('status', e.target.value)}
            className="input-field"
          >
            <option value="draft">Draft</option>
            <option value="coming_soon">Coming Soon</option>
            <option value="published">Published</option>
            <option value="activated">Activated</option>
          </select>
          <p className="mt-1 text-xs text-secondary-500">
            Draft projects are only visible to the developer. Published projects are visible to buyers.
          </p>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="btn-outline"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || !formData.developerId}
            className="btn-primary"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Project'}
          </button>
        </div>
      </form>
    </div>
  )
}
